import type { DocumentNode } from "graphql";
import type { ApolloCache, ApolloClient } from "@apollo/client";

import { overlayTwinFor } from "./overlayPairing";
import { withOptionOnInput } from "./withOptionOnInput";

const OVERLAY_ERROR_POLICIES = ["none", "ignore", "all"] as const;
type OverlayErrorPolicy = (typeof OVERLAY_ERROR_POLICIES)[number];

type OverlayRefetchQuery = {
  query: DocumentNode;
  variables?: Record<string, unknown>;
};

export type OverlayMutateArgs<
  TVariables extends Record<string, unknown>,
  TData = unknown,
> = {
  client: Pick<ApolloClient, "mutate">;
  overlayOptionId: string | undefined;
  /** Live document. Twin is looked up unless `overlayMutation` is passed. */
  mutation?: DocumentNode;
  liveMutation?: DocumentNode;
  overlayMutation?: DocumentNode;
  variables: TVariables;
  update?: (cache: ApolloCache, result: { data?: TData | null }) => void;
  errorPolicy?: OverlayErrorPolicy;
  refetchQueries?: OverlayRefetchQuery[];
  awaitRefetchQueries?: boolean;
  /** Apollo operation context, readable by links. */
  context?: Record<string, unknown>;
};

const omitLiveSearchInput = (
  variables: Record<string, unknown>
): Record<string, unknown> => {
  const overlayVariables = { ...variables };
  delete overlayVariables["searchInput"];
  return overlayVariables;
};

const resolveLiveMutation = <
  TVariables extends Record<string, unknown>,
  TData,
>({
  mutation,
  liveMutation,
}: OverlayMutateArgs<TVariables, TData>): DocumentNode => {
  const live = mutation ?? liveMutation;
  if (!live) {
    throw new Error("overlayMutate: mutation is required");
  }
  return live;
};

const resolveOverlayMutation = <
  TVariables extends Record<string, unknown>,
  TData,
>(
  liveMutation: DocumentNode,
  args: OverlayMutateArgs<TVariables, TData>
): DocumentNode => {
  const overlay = args.overlayMutation ?? overlayTwinFor(liveMutation);
  if (!overlay) {
    throw new Error("overlayMutate: no *WithOption twin registered");
  }
  return overlay;
};

export type BoundOverlayMutate = <
  TVariables extends Record<string, unknown>,
  TData = unknown,
>(
  args: Omit<OverlayMutateArgs<TVariables, TData>, "client" | "overlayOptionId">
) => Promise<{ data?: TData | null }>;

/** Route writes to overlay *WithOption when a design option is active. */
export const overlayMutate = async <
  TVariables extends Record<string, unknown>,
  TData = unknown,
>(
  args: OverlayMutateArgs<TVariables, TData>
) => {
  const {
    client,
    overlayOptionId,
    variables,
    update,
    errorPolicy,
    refetchQueries,
    awaitRefetchQueries,
    context,
  } = args;
  const liveMutation = resolveLiveMutation(args);
  const mutateOptions = {
    ...(update ? { update } : {}),
    ...(errorPolicy !== undefined ? { errorPolicy } : {}),
    ...(refetchQueries !== undefined ? { refetchQueries } : {}),
    ...(awaitRefetchQueries !== undefined ? { awaitRefetchQueries } : {}),
    ...(context !== undefined ? { context } : {}),
  };
  if (!overlayOptionId) {
    return client.mutate<TData>({
      mutation: liveMutation,
      variables,
      ...mutateOptions,
    });
  }
  return client.mutate<TData>({
    mutation: resolveOverlayMutation(liveMutation, args),
    variables: withOptionOnInput(
      omitLiveSearchInput(variables),
      overlayOptionId
    ),
    ...mutateOptions,
  });
};
